import { GraphQLErrorPolicies } from 'shared/enums/GraphQLErrorPolicies';
import { useDeleteOneAutomationMutation } from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { resetAutomationsState } from '../state/automationsSlice';
import { useHistory } from 'react-router-dom';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { getAutomationsListQuery, getGlobalAutomationsListQuery } from '../constants/operationNames';
import { useCurrentAutomationId } from './useCurrentAutomationId';
import { useGetAutomationsBasePath } from './useGetAutomationsBasePath';

export const useDeleteOneAutomation = (): {
  isDeletingAutomation: boolean;
  handleDeleteAutomation: (id: string) => Promise<void>;
} => {
  const currentAutomationId = useCurrentAutomationId();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const toast = useToast();
  const isGlobalEditor = useAppSelector((state) => state.automations.isGlobalEditor);
  const { automationsBasePath } = useGetAutomationsBasePath();

  const [deleteOneAutomation, { loading: isDeletingAutomation }] = useDeleteOneAutomationMutation({
    errorPolicy: GraphQLErrorPolicies.All,
    onCompleted: (data) => {
      const deletedAutomationId = data.deleteOneWorkflowEventSetting?.id;
      if (deletedAutomationId && deletedAutomationId === currentAutomationId) {
        dispatch(resetAutomationsState());
        history.push(automationsBasePath);
      }
    },
    refetchQueries: isGlobalEditor ? [getGlobalAutomationsListQuery] : [getAutomationsListQuery],
  });

  const handleDeleteAutomation = async (id: string): Promise<void> => {
    try {
      await deleteOneAutomation({ variables: { workflowEventSettingId: id } });
      toast({
        status: 'success',
        title: 'Success!',
        description: 'Automation deleted successfully.',
      });
    } catch (err) {
      toast({
        status: 'error',
        title: 'Error!',
        description: 'Unable to delete automation.',
      });
    }
  };

  return { handleDeleteAutomation, isDeletingAutomation };
};
